import { useUserPreferences } from '@/hooks/use-user-preferences';
import { getSttProviders } from '@/lib/stt/stt-api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
// Deepgram's nova-3-medical is the clinical default for veterinary radiology.
const CLINICAL_PROVIDER = 'deepgram';
const CLINICAL_MODEL = 'nova-3-medical';
/**
 * A provider a session may be opened on: its key is present on the server
 * and the server has not benched it after a failed connection. The one
 * filter both the resolver and the settings picker use, so they agree.
 */
export function isSttProviderUsable(provider) {
    return provider.configured && provider.available !== false;
}
/**
 * True for a session error that means the server could not reach the
 * provider (`upstream_connect: ...`) or has it benched
 * (`provider_unavailable: ...`): the provider list is stale and re-resolving
 * it moves the reader to another provider.
 */
export function isProviderUnavailableError(message) {
    return (!!message &&
        (message.startsWith('upstream_connect') || message.startsWith('provider_unavailable')));
}
/** True when the model claims the language (or is language-agnostic: ['*']). */
export function sttModelSupportsLanguage(model, language) {
    return model.languages.includes('*') || model.languages.includes(language);
}
/**
 * Best model of a provider for a language: a medical model beats the
 * provider's default, which beats the first supporting model.
 */
function bestModelFor(provider, language) {
    const supporting = provider.models.filter((m) => sttModelSupportsLanguage(m, language));
    if (supporting.length === 0)
        return undefined;
    const medical = supporting.find((m) => m.medical);
    if (medical)
        return medical.id;
    const dflt = supporting.find((m) => m.id === provider.defaultModel);
    return (dflt ?? supporting[0]).id;
}
/**
 * Picks the provider/model a dictation session should request, honoring the
 * user's speech-recognition preferences when set:
 *
 * 1. Explicit provider + model, when configured and the model covers the language.
 * 2. Explicit provider with its best model for the language (medical > default).
 * 3. Auto ranking: en -> Deepgram nova-3-medical; de/nl/es/fr -> Deepgram
 *    nova-3, then AssemblyAI multilingual, then Speechmatics.
 * 4. Any configured provider claiming the language.
 * 5. Nothing: `provider: null`. The engine reports provider_unconfigured if
 *    an arm is attempted; callers gate on `ready` so the pre-load state never
 *    reaches it.
 */
export function resolveSttProvider(providers, prefs) {
    const language = prefs?.dictationLanguage ?? 'en';
    const locale = prefs?.dictationLocale ?? null;
    const configured = providers?.filter(isSttProviderUsable) ?? [];
    const finish = (provider, model) => ({
        provider,
        model,
        language,
        locale,
        armOnDemand: configured.find((p) => p.id === provider)?.billsSessionTime ?? false,
    });
    if (configured.length === 0)
        return finish(null, '');
    // 1 + 2: an explicitly preferred provider.
    if (prefs?.dictationProvider) {
        const preferred = configured.find((p) => p.id === prefs.dictationProvider);
        if (preferred) {
            const explicitModel = prefs.dictationModel
                ? preferred.models.find((m) => m.id === prefs.dictationModel)
                : undefined;
            if (explicitModel && sttModelSupportsLanguage(explicitModel, language)) {
                return finish(preferred.id, explicitModel.id);
            }
            // Explicit model missing or not covering the language: the provider's
            // best model for the language (e.g. nova-3-medical downgrades to
            // nova-3 for German).
            const best = bestModelFor(preferred, language);
            if (best)
                return finish(preferred.id, best);
        }
    }
    // 3: auto ranking.
    const ranked = language === 'en'
        ? [[CLINICAL_PROVIDER, CLINICAL_MODEL]]
        : [
            [CLINICAL_PROVIDER, 'nova-3'],
            ['assemblyai', 'universal-streaming-multilingual'],
            ['speechmatics', undefined],
        ];
    for (const [providerId, modelId] of ranked) {
        // react-doctor-disable-next-line react-doctor/js-index-maps -- at most three ranked entries looked up in a handful of configured providers
        const provider = configured.find((p) => p.id === providerId);
        if (!provider)
            continue;
        const model = modelId
            ? provider.models.find((m) => m.id === modelId && sttModelSupportsLanguage(m, language))?.id
            : bestModelFor(provider, language);
        if (model)
            return finish(provider.id, model);
    }
    // 4: any configured provider claiming the language.
    for (const provider of configured) {
        const model = bestModelFor(provider, language);
        if (model)
            return finish(provider.id, model);
    }
    // 5: nothing server-side covers the language.
    return finish(null, '');
}
const STT_PROVIDERS_QUERY_KEY = ['stt-providers'];
/** Fetches GET /stt/providers (cached). */
export function useSttProviders(enabled) {
    return useQuery({
        queryKey: STT_PROVIDERS_QUERY_KEY,
        queryFn: getSttProviders,
        staleTime: 5 * 60_000,
        enabled,
    });
}
/**
 * Fetches the server's configured STT providers (cached), reads the user's
 * speech-recognition preferences, and returns the provider/model/language the
 * dictation session should request. Callers gate the engine on `ready`.
 */
export function useResolvedSttProvider(enabled) {
    const { data } = useSttProviders(enabled);
    const { preferences } = useUserPreferences();
    const reportSettings = preferences.reportSettings;
    const ready = !enabled || data !== undefined;
    const queryClient = useQueryClient();
    const refreshProviders = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: STT_PROVIDERS_QUERY_KEY });
    }, [queryClient]);
    return useMemo(() => ({ ...resolveSttProvider(data, reportSettings), ready, refreshProviders }), [data, reportSettings, ready, refreshProviders]);
}
